import * as THREE from 'three';
import { Entity } from './Entity.js';
import type { AABB } from '../systems/Collision.js';
import type { CoverType } from '../level/Generator.js';

/**
 * Cover entity - destructible obstacles that provide tactical positioning.
 *
 * Cover types:
 * - Crate: Small box, brown, low HP
 * - Pillar: Tall cylinder, gray, medium HP
 * - Debris: Irregular shape, dark gray, medium HP
 *
 * Cover can be destroyed by projectiles, creating dynamic battlefield changes.
 */
export class Cover extends Entity {
  private hp: number;
  private maxHP: number;
  private type: CoverType;

  // Collision box dimensions
  private width: number;
  private height: number;

  constructor(x: number, y: number, type: CoverType) {
    super(x, y, 0);

    this.type = type;

    // Set HP and dimensions based on type
    switch (type) {
      case 'crate':
        this.maxHP = 20;
        this.width = 1;
        this.height = 1;
        break;
      case 'pillar':
        this.maxHP = 40;
        this.width = 0.8;
        this.height = 0.8;
        break;
      case 'debris':
        this.maxHP = 30;
        this.width = 1.2;
        this.height = 0.8;
        break;
    }

    this.hp = this.maxHP;

    // Create visual representation
    this.createMesh();
    this.updateMeshTransform();
  }

  /**
   * Create the visual mesh for this cover type.
   */
  private createMesh(): void {
    let geometry: THREE.BufferGeometry;
    let material: THREE.Material;

    switch (this.type) {
      case 'crate':
        // Box shape, brown/tan
        geometry = new THREE.BoxGeometry(this.width, 1.0, this.height);
        material = new THREE.MeshStandardMaterial({
          color: 0x8b7355, // Brown
          roughness: 0.9,
          metalness: 0.1,
        });
        break;

      case 'pillar':
        // Cylinder shape, gray
        geometry = new THREE.CylinderGeometry(
          this.width / 2,
          this.width / 2,
          1.5, // Taller than crate
          8 // Low poly count for aesthetic
        );
        material = new THREE.MeshStandardMaterial({
          color: 0x5a5a6a, // Gray
          roughness: 0.7,
          metalness: 0.4,
        });
        break;

      case 'debris':
        // Irregular box shape, dark gray
        geometry = new THREE.BoxGeometry(this.width, 0.6, this.height);
        material = new THREE.MeshStandardMaterial({
          color: 0x3a3a48, // Dark gray
          roughness: 0.8,
          metalness: 0.2,
        });
        break;
    }

    this.mesh = new THREE.Mesh(geometry, material);

    // Position mesh slightly above ground
    if (this.mesh) {
      this.mesh.position.y = this.type === 'pillar' ? 0.75 : 0.5;
    }
  }

  /**
   * Take damage and potentially destroy the cover.
   *
   * @param amount - Amount of damage to take
   * @returns True if cover was destroyed, false otherwise
   */
  takeDamage(amount: number): boolean {
    this.hp -= amount;

    // Update visual appearance based on damage
    this.updateDamageVisual();

    if (this.hp <= 0) {
      this.hp = 0;
      this.destroy();
      return true;
    }

    return false;
  }

  /**
   * Update the visual appearance based on current damage state.
   *
   * Darkens the color and reduces opacity as HP decreases.
   */
  private updateDamageVisual(): void {
    if (!this.mesh || !(this.mesh instanceof THREE.Mesh)) {
      return;
    }

    const material = this.mesh.material as THREE.MeshStandardMaterial;
    const hpPercent = this.hp / this.maxHP;

    // Darken as it takes damage
    const baseBrightness = hpPercent * 0.3 + 0.7; // 0.7 to 1.0

    // Get base color for this type
    let baseColor: number;
    switch (this.type) {
      case 'crate':
        baseColor = 0x8b7355;
        break;
      case 'pillar':
        baseColor = 0x5a5a6a;
        break;
      case 'debris':
        baseColor = 0x3a3a48;
        break;
    }

    // Extract RGB components
    const r = ((baseColor >> 16) & 0xff) / 255;
    const g = ((baseColor >> 8) & 0xff) / 255;
    const b = (baseColor & 0xff) / 255;

    // Darken based on damage
    material.color.setRGB(
      r * baseBrightness,
      g * baseBrightness,
      b * baseBrightness
    );

    // Make slightly transparent when heavily damaged
    if (hpPercent < 0.3) {
      material.transparent = true;
      material.opacity = 0.7 + hpPercent * 0.3;
    }
  }

  /**
   * Get the collision shape for this cover.
   *
   * Uses an AABB (axis-aligned bounding box) for collision detection.
   *
   * @returns AABB collision shape
   */
  getCollisionShape(): AABB {
    return {
      type: 'aabb',
      x: this.position.x,
      y: this.position.y,
      width: this.width,
      height: this.height,
    };
  }

  /**
   * Get current HP.
   */
  getHP(): number {
    return this.hp;
  }

  /**
   * Get maximum HP.
   */
  getMaxHP(): number {
    return this.maxHP;
  }

  /**
   * Get cover type.
   */
  getType(): CoverType {
    return this.type;
  }

  /**
   * Update cover state.
   *
   * Cover is static, so no update logic needed.
   *
   * @param dt - Fixed delta time in milliseconds
   */
  update(dt: number): void {
    // Cover is static, no update needed
  }

  /**
   * Synchronize mesh transform with entity position.
   */
  protected updateMeshTransform(): void {
    if (this.mesh) {
      // Map 2D game coordinates to Three.js 3D space
      this.mesh.position.set(
        this.position.x,
        this.type === 'pillar' ? 0.75 : 0.5, // Keep height offset
        this.position.y
      );

      // No rotation for cover
      this.mesh.rotation.y = 0;
    }
  }
}
